import React, { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authService, setAuthService] = useState(null);

  useEffect(() => {
    let isMounted = true;

    // Initialize auth state with better error handling
    const initializeAuth = async () => {
      try {
        setLoading(true);
        setAuthError(null);

        // Dynamically import authService
        try {
          const authModule = await import("../utils/authService");
          const importedAuthService = authModule?.default;
          
          if (isMounted) {
            setAuthService(importedAuthService);
          }

          // Check if authService is available and properly initialized
          if (!importedAuthService || typeof importedAuthService !== 'object') {
            console.warn('Auth service not properly initialized');
            if (isMounted) {
              setAuthError("Authentication service not available");
              setLoading(false);
            }
            return;
          }

          // Check if required methods exist
          if (typeof importedAuthService?.getSession !== 'function') {
            console.warn('Auth service getSession method not available');
            if (isMounted) {
              setAuthError("Authentication methods not available");
              setLoading(false);
            }
            return;
          }

          // Enhanced session retrieval with multiple attempts
          let sessionResult = null;
          let attempts = 0;
          const maxAttempts = 3;
          
          while (!sessionResult?.success && attempts < maxAttempts) {
            attempts++;
            console.log(`Auth initialization attempt ${attempts}...`);
            
            try {
              sessionResult = await importedAuthService?.getSession();
              if (sessionResult?.success) {
                break;
              }
            } catch (sessionError) {
              console.warn(`Session attempt ${attempts} failed:`, sessionError);
              if (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
          }

          if (
            sessionResult?.success &&
            sessionResult?.data?.session?.user &&
            isMounted
          ) {
            const authUser = sessionResult?.data?.session?.user;
            setUser(authUser);

            // Fetch user profile with error handling
            try {
              if (typeof importedAuthService?.getUserProfile === 'function') {
                const profileResult = await importedAuthService?.getUserProfile(authUser?.id);

                if (profileResult?.success && isMounted) {
                  setUserProfile(profileResult?.data);
                } else if (isMounted) {
                  // Don't set error for profile fetch failures, just log
                  console.log("Profile fetch warning:", profileResult?.error);
                }
              }
            } catch (profileError) {
              console.log("Profile fetch error:", profileError);
            }
          } else {
            console.log('No valid session found during initialization');
          }
        } catch (importError) {
          console.warn("Failed to import auth service:", importError);
          if (isMounted) {
            setAuthError("Authentication service temporarily unavailable");
            setLoading(false);
          }
          return;
        }
      } catch (error) {
        if (isMounted) {
          console.log("Auth initialization error:", error);
          // Only set error for critical failures
          if (error?.message?.includes('not available') || error?.message?.includes('not properly initialized')) {
            setAuthError("Authentication service unavailable");
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Enhanced auth state change listener
    let subscription;
    const setupAuthListener = async () => {
      try {
        if (authService && typeof authService?.onAuthStateChange === 'function') {
          const authListener = authService?.onAuthStateChange(async (event, session) => {
            if (!isMounted) return;

            console.log('Auth state change:', event, !!session?.user);
            setAuthError(null);

            if (event === "SIGNED_IN" && session?.user) {
              setUser(session?.user);

              // Fetch user profile for signed in user
              try {
                if (typeof authService?.getUserProfile === 'function') {
                  const profileResult = await authService?.getUserProfile(session?.user?.id);
                  if (profileResult?.success && isMounted) {
                    setUserProfile(profileResult?.data);
                  }
                }
              } catch (error) {
                console.log("Profile fetch error on sign in:", error);
              }
            } else if (event === "SIGNED_OUT") {
              setUser(null);
              setUserProfile(null);
            } else if (event === "TOKEN_REFRESHED" && session?.user) {
              setUser(session?.user);
            } else if (event === "USER_UPDATED" && session?.user) {
              setUser(session?.user);
            }
          });

          subscription = authListener?.data?.subscription;
        }
      } catch (error) {
        console.log("Auth listener setup error:", error);
      }
    };

    // Set up listener after authService is loaded
    if (authService) {
      setupAuthListener();
    }

    return () => {
      isMounted = false;
      if (subscription && typeof subscription?.unsubscribe === 'function') {
        subscription?.unsubscribe();
      }
    };
  }, [authService]);

  // Sign in function
  const signIn = async (email, password) => {
    try {
      setAuthError(null);

      if (!authService || typeof authService?.signIn !== 'function') {
        const errorMsg = "Authentication service not available";
        setAuthError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const result = await authService?.signIn(email, password);

      if (!result?.success) {
        setAuthError(result?.error || "Login failed");
        return { success: false, error: result?.error };
      }

      return { success: true, data: result?.data };
    } catch (error) {
      const errorMsg = "Something went wrong during login. Please try again.";
      setAuthError(errorMsg);
      console.log("Sign in error:", error);
      return { success: false, error: errorMsg };
    }
  };

  // Enhanced sign up function with better error handling
  const signUp = async (email, password, userData = {}) => {
    try {
      setAuthError(null);
      setLoading(true);

      if (!authService || typeof authService?.signUp !== 'function') {
        const errorMsg = "Authentication service not available";
        setAuthError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const result = await authService?.signUp(email, password, userData);

      if (!result?.success) {
        setAuthError(result?.error || "Signup failed");
        return { success: false, error: result?.error };
      }

      // Enhanced: Wait for user creation to propagate
      if (result?.data?.user) {
        console.log('User created, waiting for auth state propagation...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Force a session refresh to ensure user is available immediately
        try {
          const { data: { session }, error } = await authService?.getSession();
          if (!error && session?.user) {
            setUser(session?.user);
          }
        } catch (sessionError) {
          console.warn('Session refresh after signup failed:', sessionError);
        }
      }

      return { success: true, data: result?.data };
    } catch (error) {
      const errorMsg = "Something went wrong during signup. Please try again.";
      setAuthError(errorMsg);
      console.log("Sign up error:", error);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      setAuthError(null);

      if (!authService || typeof authService?.signOut !== 'function') {
        const errorMsg = "Authentication service not available";
        setAuthError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const result = await authService?.signOut();

      if (!result?.success) {
        setAuthError(result?.error || "Logout failed");
        return { success: false, error: result?.error };
      }

      return { success: true };
    } catch (error) {
      const errorMsg = "Something went wrong during logout. Please try again.";
      setAuthError(errorMsg);
      console.log("Sign out error:", error);
      return { success: false, error: errorMsg };
    }
  };

  // Update profile function
  const updateProfile = async (updates) => {
    try {
      setAuthError(null);

      if (!user?.id) {
        const errorMsg = "User not authenticated";
        setAuthError(errorMsg);
        return { success: false, error: errorMsg };
      }

      if (!authService || typeof authService?.updateUserProfile !== 'function') {
        const errorMsg = "Authentication service not available";
        setAuthError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const result = await authService?.updateUserProfile(user?.id, updates);

      if (!result?.success) {
        setAuthError(result?.error || "Profile update failed");
        return { success: false, error: result?.error };
      }

      setUserProfile(result?.data);
      return { success: true, data: result?.data };
    } catch (error) {
      const errorMsg =
        "Something went wrong updating profile. Please try again.";
      setAuthError(errorMsg);
      console.log("Update profile error:", error);
      return { success: false, error: errorMsg };
    }
  };

  // Reset password function
  const resetPassword = async (email) => {
    try {
      setAuthError(null);

      if (!authService || typeof authService?.resetPassword !== 'function') {
        const errorMsg = "Authentication service not available";
        setAuthError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const result = await authService?.resetPassword(email);

      if (!result?.success) {
        setAuthError(result?.error || "Password reset failed");
        return { success: false, error: result?.error };
      }

      return { success: true };
    } catch (error) {
      const errorMsg =
        "Something went wrong sending reset email. Please try again.";
      setAuthError(errorMsg);
      console.log("Reset password error:", error);
      return { success: false, error: errorMsg };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    authError,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    clearError: () => setAuthError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;